'use client'
// DealWidget — embeddable flight deal widget for blog posts.
// Shows a 2-month price-heatmap calendar + top deal cards + CTA.
// Green = cheapest dates, amber = mid, red = expensive.

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { AIRPORT_DB } from '@/data/airports'
import { currencySymbol, useCurrency } from '@/lib/currency'

// ── Module-level deal cache (single fetch per session) ───────────
let _cache = null
let _promise = null

function fetchDeals() {
  if (_cache) return Promise.resolve(_cache)
  if (_promise) return _promise
  _promise = fetch('/api/deals?limit=30&sort=score')
    .then(r => r.ok ? r.json() : { deals: [] })
    .then(d => { _cache = Array.isArray(d.deals) ? d.deals : []; return _cache })
    .catch(() => { _cache = []; return _cache })
  return _promise
}

// ── Price → heatmap colour ────────────────────────────────────────
function heatColor(price, min, max) {
  if (max === min) return '#22c55e'
  const t = (price - min) / (max - min) // 0=cheapest, 1=priciest
  if (t < 0.35) return '#16a34a'  // green-700
  if (t < 0.65) return '#d97706'  // amber-600
  return '#dc2626'                  // red-600
}

// ── Build date → {price, id} map (lowest price per date) ─────────
function buildMap(deals) {
  const map = {}
  for (const deal of deals) {
    if (!deal.departureDate) continue
    const d = deal.departureDate
    if (!map[d] || deal.price < map[d].price) {
      map[d] = { price: deal.price, id: deal.id, currency: deal.currency }
    }
    // Also mark multi-date ranges
    if (deal.date_range_start && deal.date_count > 1) {
      // skip range expansion — too heavy; departure date covers the cheapest date
    }
  }
  return map
}

// ── Single month calendar ─────────────────────────────────────────
function MonthCal({ year, month, map, min, max, formatDealPrice }) {
  const daysInMonth = new Date(year, month, 0).getDate()
  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7 // Mon-first
  const label = new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  const today = new Date(); today.setHours(0, 0, 0, 0)

  const cells = []
  for (let i = 0; i < firstDow; i++) cells.push(<div key={`e${i}`} />)

  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    const entry = map[dateStr]
    const isPast = new Date(year, month - 1, d) < today

    if (entry && !isPast) {
      const col = heatColor(entry.price, min, max)
      cells.push(
        <Link
          key={d}
          href={`/deals/${entry.id}`}
          className="dw-cell dw-deal"
          style={{ background: col + '22', color: col, borderColor: col + '77' }}
          title={`Fly ${dateStr} from ${formatDealPrice ? formatDealPrice(entry.price).primary : `${currencySymbol(entry.currency)}${entry.price}`}`}
        >
          <span className="dw-day">{d}</span>
          <span className="dw-price">{formatDealPrice ? formatDealPrice(entry.price).primary : `${currencySymbol(entry.currency)}${entry.price}`}</span>
        </Link>
      )
    } else {
      cells.push(
        <div key={d} className={`dw-cell${isPast ? ' dw-past' : ''}`}>
          <span className="dw-day">{d}</span>
        </div>
      )
    }
  }

  return (
    <div className="dw-month">
      <div className="dw-month-label">{label}</div>
      <div className="dw-grid">
        {['Mo','Tu','We','Th','Fr','Sa','Su'].map(h => (
          <div key={h} className="dw-dow">{h}</div>
        ))}
        {cells}
      </div>
    </div>
  )
}

// ── Mini deal card ────────────────────────────────────────────────
function MiniCard({ deal, formatDealPrice }) {
  const originCity = AIRPORT_DB[deal.origin]?.city ?? deal.origin
  const destCity   = AIRPORT_DB[deal.destination]?.city ?? deal.destination
  const [y, m, d]  = (deal.departureDate || '').split('-').map(Number)
  const dateLabel  = y ? new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
  const priceDisplay = formatDealPrice ? formatDealPrice(deal.price).primary : `${currencySymbol(deal.currency)}${deal.price}`

  return (
    <Link href={`/deals/${deal.id}`} className="dw-card">
      <div className="dw-card-route">{originCity} → {destCity}</div>
      <div className="dw-card-price">{priceDisplay}</div>
      {deal.discountPct > 0 && (
        <div className="dw-card-badge">−{deal.discountPct}%</div>
      )}
      {deal.airline && <div className="dw-card-airline">{deal.airline}</div>}
      {dateLabel && <div className="dw-card-date">Dep. {dateLabel}</div>}
      <div className="dw-card-cta">View deal →</div>
    </Link>
  )
}

// ── Main widget ───────────────────────────────────────────────────
export default function DealWidget({ origin = null, destination = null }) {
  const { formatDealPrice } = useCurrency()
  const [deals, setDeals] = useState(null) // null = loading

  useEffect(() => {
    fetchDeals().then(all => {
      let d = all
      if (origin)      d = d.filter(x => x.origin === origin)
      if (destination) d = d.filter(x => x.destination === destination)
      setDeals(d)
    })
  }, [origin, destination])

  if (deals === null) {
    return (
      <div className="dw-root dw-loading" aria-busy="true">
        Loading live deals…
      </div>
    )
  }
  if (deals.length === 0) return null

  const map     = buildMap(deals)
  const prices  = Object.values(map).map(e => e.price)
  const min     = Math.min(...prices)
  const max     = Math.max(...prices)

  const now     = new Date()
  const y0 = now.getFullYear(), m0 = now.getMonth() + 1
  const m1 = m0 === 12 ? 1 : m0 + 1
  const y1 = m0 === 12 ? y0 + 1 : y0

  const topDeals = [...deals]
    .sort((a, b) => (b.discountPct ?? 0) - (a.discountPct ?? 0))
    .slice(0, 3)

  return (
    <div className="dw-root" aria-label="Live flight deals widget">
      <div className="dw-header">
        <span className="dw-title">✈ Live flight deals</span>
        <span className="dw-legend">
          <span style={{ color: '#16a34a' }}>● Cheap</span>
          <span style={{ color: '#d97706' }}>● Mid</span>
          <span style={{ color: '#dc2626' }}>● Expensive</span>
        </span>
      </div>

      <div className="dw-calendars">
        <MonthCal year={y0} month={m0} map={map} min={min} max={max} formatDealPrice={formatDealPrice} />
        <MonthCal year={y1} month={m1} map={map} min={min} max={max} formatDealPrice={formatDealPrice} />
      </div>

      <div className="dw-cards">
        {topDeals.map(d => <MiniCard key={d.id} deal={d} formatDealPrice={formatDealPrice} />)}
      </div>

      <div className="dw-footer">
        <Link href="/deals" className="dw-cta-btn">
          See all deals →
        </Link>
      </div>
    </div>
  )
}
